"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SessionEngineCore = void 0;
const sdk_geometry_api_sdk_v2_1 = require("@shapediver/sdk.geometry-api-sdk-v2");
const viewer_settings_1 = require("@shapediver/viewer.settings");
const viewer_shared_node_tree_1 = require("@shapediver/viewer.shared.node-tree");
const viewer_shared_services_1 = require("@shapediver/viewer.shared.services");
const CustomizationManager_1 = require("./managers/CustomizationManager");
const ExportManager_1 = require("./managers/ExportManager");
const FileUploadManager_1 = require("./managers/FileUploadManager");
const ModelStateManager_1 = require("./managers/ModelStateManager");
const OutputManager_1 = require("./managers/OutputManager");
const ParameterManager_1 = require("./managers/ParameterManager");
const SettingsManager_1 = require("./managers/SettingsManager");
const UtilsManager_1 = require("./managers/UtilsManager");
/**
 * Core class of the Session Engine.
 *
 * The SessionEngineCore is the main entry point to create and manage sessions.
 * The manager instances for parameters, outputs, exports, etc. can be accessed via their respective properties.
 */
class SessionEngineCore {
    /**
     * Can be use to initialize a session with the ticket/guid and modelViewUrl and returns a scene graph node with the result.
     * Can be use to customize the session with updated parameters to get the updated scene graph node.
     */
    constructor(properties) {
        this._eventEngine = viewer_shared_services_1.EventEngine.instance;
        this._httpClient = viewer_shared_services_1.HttpClient.instance;
        this._performanceEvaluator = viewer_shared_services_1.PerformanceEvaluator.instance;
        this._sessionEngineId = viewer_shared_services_1.UuidGenerator.instance.create();
        this._automaticSceneUpdate = true;
        this._closeOnFailure = () => __awaiter(this, void 0, void 0, function* () { });
        this._closed = false;
        this._excludeViewports = [];
        this._headers = {
            "X-ShapeDiver-Origin": viewer_shared_services_1.SystemInfo.instance.origin,
            "X-ShapeDiver-SessionEngineId": this._sessionEngineId,
            "X-ShapeDiver-BuildVersion": "",
            "X-ShapeDiver-BuildDate": "",
        };
        this._initialized = false;
        this._loadSdtf = false;
        this._updateCallback = null;
        this._utilsManager = new UtilsManager_1.UtilsManager(this);
        this._settingsManager = new SettingsManager_1.SettingsManager(this);
        this._outputManager = new OutputManager_1.OutputManager(this);
        this._exportManager = new ExportManager_1.ExportManager(this);
        this._fileUploadManager = new FileUploadManager_1.FileUploadManager(this);
        this._parameterManager = new ParameterManager_1.ParameterManager(this);
        this._customizationManager = new CustomizationManager_1.CustomizationManager(this);
        this._modelStateManager = new ModelStateManager_1.ModelStateManager(this);
        this._id = properties.id;
        this._node = new viewer_shared_node_tree_1.TreeNode(properties.id);
        this._guid = properties.guid;
        this._ticket = properties.ticket;
        this._modelViewUrl = properties.modelViewUrl;
        this._excludeViewports = properties.excludeViewports || [];
        this._jwtToken = properties.jwtToken;
        this._outputManager.allowOutputLoading = properties.allowOutputLoading;
        this._parameterManager.ignoreUnknownParams =
            properties.ignoreUnknownParams;
        this._loadSdtf = properties.loadSdtf;
        this._modelStateManager.modelStateId = properties.modelStateId;
        this._modelStateManager.modelStateValidationMode =
            properties.modelStateValidationMode;
        this._customizationManager.throwOnCustomizationError =
            properties.throwOnCustomizationError !== undefined
                ? properties.throwOnCustomizationError
                : false;
        this._headers["X-ShapeDiver-BuildDate"] = properties.buildDate;
        this._headers["X-ShapeDiver-BuildVersion"] = properties.buildVersion;
        try {
            this._sdkConfig = new sdk_geometry_api_sdk_v2_1.Configuration({
                basePath: this._modelViewUrl,
                accessToken: this._jwtToken,
                baseOptions: {
                    headers: this._headers,
                },
            });
        }
        catch (e) {
            this._utilsManager.handleError(e).then((convertedError) => {
                throw convertedError;
            });
        }
    }
    get automaticSceneUpdate() {
        return this._automaticSceneUpdate;
    }
    set automaticSceneUpdate(value) {
        this._automaticSceneUpdate = value;
        value && this._closed === false
            ? this._utilsManager.addToSceneTree(this._node)
            : this._utilsManager.removeFromSceneTree(this._node);
    }
    get closeOnFailure() {
        return this._closeOnFailure;
    }
    get closed() {
        return this._closed;
    }
    get customizationManager() {
        return this._customizationManager;
    }
    get excludeViewports() {
        return this._excludeViewports;
    }
    set excludeViewports(value) {
        this._excludeViewports = JSON.parse(JSON.stringify(value));
        this._node.excludeViewports = JSON.parse(JSON.stringify(value));
    }
    get exportManager() {
        return this._exportManager;
    }
    get fileUploadManager() {
        return this._fileUploadManager;
    }
    get guid() {
        return this._guid;
    }
    get id() {
        return this._id;
    }
    get initialized() {
        return this._initialized;
    }
    set initialized(value) {
        this._initialized = value;
    }
    get jwtToken() {
        return this._jwtToken;
    }
    get loadSdtf() {
        return this._loadSdtf;
    }
    set loadSdtf(value) {
        this._loadSdtf = value;
        if (this._initialized === true && this._loadSdtf === true) {
            (() => __awaiter(this, void 0, void 0, function* () {
                this._outputManager.reloadSdtf = true;
                yield this._outputManager.updateOutputs();
                this._outputManager.reloadSdtf = false;
                this._eventEngine.emitEvent(viewer_shared_services_1.EVENTTYPE.SESSION.SESSION_SDTF_DELAYED_LOADED, { sessionId: this.id });
            }))();
        }
    }
    get modelId() {
        return this._modelId;
    }
    get modelStateManager() {
        return this._modelStateManager;
    }
    get modelViewUrl() {
        return this._modelViewUrl;
    }
    get node() {
        return this._node;
    }
    set node(value) {
        this._node = value;
    }
    get outputManager() {
        return this._outputManager;
    }
    get parameterManager() {
        return this._parameterManager;
    }
    get refreshJwtToken() {
        return this._refreshJwtToken;
    }
    set refreshJwtToken(value) {
        this._refreshJwtToken = value;
    }
    get responseDto() {
        return this._responseDto;
    }
    get sdkConfig() {
        return this._sdkConfig;
    }
    get sessionId() {
        return this._sessionId;
    }
    get settingsManager() {
        return this._settingsManager;
    }
    get ticket() {
        return this._ticket;
    }
    get updateCallback() {
        return this._updateCallback;
    }
    set updateCallback(value) {
        this._updateCallback = value;
    }
    get utilsManager() {
        return this._utilsManager;
    }
    /**
     * Closes the session.
     *
     * @param retry whether to retry closing the session in case of failure
     */
    close() {
        return __awaiter(this, arguments, void 0, function* (retry = false) {
            this._utilsManager.checkAvailability("close");
            try {
                this._httpClient.removeDataLoading(this._sessionId);
                yield new sdk_geometry_api_sdk_v2_1.SessionApi(this._sdkConfig).closeSession(this._sessionId);
                if (this._automaticSceneUpdate)
                    this._utilsManager.removeFromSceneTree(this._node);
                this._closed = true;
            }
            catch (e) {
                yield this._utilsManager.handleError(e, retry);
                return yield this.close(true);
            }
        });
    }
    /**
     * Initializes the session with the ticket and modelViewUrl.
     *
     * @returns promise with a scene graph node
     */
    init(parameterValues_1) {
        return __awaiter(this, arguments, void 0, function* (parameterValues, retry = false) {
            var _a, _b;
            if (this._initialized === true)
                throw new viewer_shared_services_1.ShapeDiverViewerSessionError("Session.init: Session already initialized.");
            try {
                this._performanceEvaluator.startSection("sessionResponse.init");
                const parameterSet = {};
                // the slice here is done as a way for deep copying the string values
                for (const parameterNameOrId in parameterValues)
                    parameterSet[parameterNameOrId] = (" " + parameterValues[parameterNameOrId]).slice(1);
                if (this._ticket) {
                    this._responseDto = (yield new sdk_geometry_api_sdk_v2_1.SessionApi(this._sdkConfig).createSessionByTicket(this._ticket, this._modelStateManager.modelStateId, this._parameterManager.ignoreUnknownParams, this._modelStateManager.modelStateValidationMode, parameterSet)).data;
                }
                else if (this._guid) {
                    this._responseDto = (yield new sdk_geometry_api_sdk_v2_1.SessionApi(this._sdkConfig).createSessionByModel(this._guid, this._modelStateManager.modelStateId, this._parameterManager.ignoreUnknownParams, this._modelStateManager.modelStateValidationMode, parameterSet)).data;
                }
                else {
                    // we should never get here
                    throw new viewer_shared_services_1.ShapeDiverViewerSessionError("Session.init: Initialization of session failed. Neither a ticket nor a guid are available.");
                }
                this._performanceEvaluator.endSection("sessionResponse.init");
                this._settingsManager.viewerSettings =
                    (_a = this._responseDto.viewer) === null || _a === void 0 ? void 0 : _a.config;
                this._settingsManager.viewerSettingsVersionBackend =
                    this._responseDto.viewerSettingsVersion || viewer_settings_1.latestVersion;
                this._sessionId = this._responseDto.sessionId;
                this._modelId = (_b = this._responseDto.model) === null || _b === void 0 ? void 0 : _b.id;
                this._modelStateManager.modelState = this._responseDto
                    .modelState;
                this._httpClient.addDataLoading(this._sessionId, {
                    getAsset: (url) => __awaiter(this, void 0, void 0, function* () {
                        const response = yield new sdk_geometry_api_sdk_v2_1.UtilsApi(this._sdkConfig).downloadAsset(url, {
                            responseType: "arraybuffer",
                        })[0];
                        return [
                            response.data,
                            response.headers["content-type"],
                        ];
                    }),
                    downloadTexture: (url) => __awaiter(this, void 0, void 0, function* () {
                        try {
                            const response = yield new sdk_geometry_api_sdk_v2_1.UtilsApi(this._sdkConfig).downloadImage(this._sessionId, url, {
                                responseType: "arraybuffer",
                            });
                            return [
                                response.data,
                                response.headers["content-type"],
                            ];
                        }
                        catch (e) {
                            throw yield this._utilsManager.handleError(e);
                        }
                    }),
                });
                this._settingsManager.settingsEngine.loadSettings(this._settingsManager.viewerSettings);
                if (!this._sessionId)
                    throw new viewer_shared_services_1.ShapeDiverViewerSessionError("Session.init: Initialization of session failed. ResponseDto did not have a sessionId.");
                if (!this._modelId)
                    throw new viewer_shared_services_1.ShapeDiverViewerSessionError("Session.init: Initialization of session failed. ResponseDto did not have a model.id.");
                this.updateResponseDto(this._responseDto, parameterSet);
                this._initialized = true;
            }
            catch (e) {
                yield this._utilsManager.handleError(e, retry);
                return yield this.init(parameterValues, true);
            }
        });
    }
    /**
     * Sets the JWT token for the session.
     *
     * @param value the JWT token
     * @param retry whether to retry setting the token in case of failure
     */
    setJwtToken(value_1) {
        return __awaiter(this, arguments, void 0, function* (value, retry = false) {
            this._utilsManager.checkAvailability();
            this._jwtToken = value;
            try {
                this._sdkConfig.accessToken = value;
                const responseDto = (yield new sdk_geometry_api_sdk_v2_1.SessionApi(this._sdkConfig).getSessionDefaults(this._sessionId)).data;
                if (this._responseDto)
                    this._responseDto.actions = responseDto.actions;
            }
            catch (e) {
                yield this._utilsManager.handleError(e, retry);
                yield this.setJwtToken(value, true);
            }
        });
    }
    /**
     * Updates the response DTO of the session engine core.
     *
     * @param responseDto The new response DTO
     * @param initialParameters Optional initial parameters to consider when updating
     */
    updateResponseDto(responseDto, initialParameters) {
        if (!this._responseDto) {
            this._responseDto = responseDto;
            return;
        }
        // convert parameters
        if (responseDto.parameters) {
            for (const parameterId in responseDto.parameters) {
                this._responseDto.parameters =
                    this._responseDto.parameters || {};
                this._responseDto.parameters[parameterId] =
                    this._responseDto.parameters[parameterId] ||
                        responseDto.parameters[parameterId];
            }
        }
        // convert outputs
        if (responseDto.outputs) {
            for (const outputId in responseDto.outputs) {
                this._responseDto.outputs = this._responseDto.outputs || {};
                if ("version" in responseDto.outputs[outputId] ||
                    !(this._responseDto.outputs[outputId] &&
                        "version" in this._responseDto.outputs[outputId]))
                    this._responseDto.outputs[outputId] =
                        responseDto.outputs[outputId];
            }
        }
        // convert exports
        if (responseDto.exports) {
            for (const exportId in responseDto.exports) {
                this._responseDto.exports = this._responseDto.exports || {};
                if ("version" in responseDto.exports[exportId] ||
                    !(this._responseDto.exports[exportId] &&
                        "version" in this._responseDto.exports[exportId]))
                    this._responseDto.exports[exportId] =
                        responseDto.exports[exportId];
            }
        }
        this._parameterManager.createParametersFromDto(initialParameters);
        this._exportManager.createExportsFromDto();
        this._outputManager.createOutputsFromDto();
    }
}
exports.SessionEngineCore = SessionEngineCore;
//# sourceMappingURL=SessionEngineCore.js.map